import React from 'react';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {ForecastTooltipFormulaRenderer, type TooltipFormulaItem} from '../../ForecastTooltipFormulaRenderer';
import {FinancialCategoryTooltip} from '../../../forecast-app/shared/components/tooltips/FinancialCategoryTooltip';
import {IntlShape} from 'react-intl';
import {useIntl} from 'react-intl';

type ProjectPortfolioReportTooltipProps = React.PropsWithChildren<{
	items: TooltipFormulaItem[];
	size?: string;
}>;

const hasTooltipItemTitleOnly = (item: TooltipFormulaItem) => !!item.title && !item.description && !item.details;

// TODO: This tooltip can be removed when the FF `financial_categories_update` is rolled out.
export const ProjectPortfolioReportTooltip = ({
	items,
	size,
	children,
}: ProjectPortfolioReportTooltipProps): React.JSX.Element => {
	if (!items || items.length === 0) {
		return <>{children}</>;
	}

	const hasFinancialCategories = hasFeatureFlag('financial_categories_update');

	if (!hasFinancialCategories) {
		const isArrayofStrings = items.every(item => hasTooltipItemTitleOnly(item));
		const basicTooltipContent = isArrayofStrings ? [items.map(item => item.title)] : undefined;

		return (
			<ForecastTooltip
				maxWidth={isArrayofStrings ? undefined : 'none'}
				content={basicTooltipContent || <ForecastTooltipFormulaRenderer items={items} translatedMessage={true} />}
			>
				{children}
			</ForecastTooltip>
		);
	}

	return (
		<FinancialCategoryTooltip items={items} size={size}>
			{children}
		</FinancialCategoryTooltip>
	);
};

export const createProjectPortfolioReportTooltip = (
	itemsFn: (formatMessage: IntlShape['formatMessage']) => TooltipFormulaItem[]
) => {
	return (props: Omit<ProjectPortfolioReportTooltipProps, 'items'>): React.JSX.Element => {
		const {formatMessage} = useIntl();
		return <ProjectPortfolioReportTooltip {...props} items={itemsFn(formatMessage)} />;
	};
};
